(function () {
  'use strict';

  const CONFIG = window.BHC_CONFIG || {};
  const API_URL = CONFIG.apiUrl;
  if (!API_URL) console.error('[bh-curve-viewer] window.BHC_CONFIG.apiUrl is not set.');

  const root = document.getElementById('bhc-app');
  if (!root) return;
  const $ = (id) => root.querySelector('#' + id);

  const KOE_TO_KAM = 79.5775;

  // Fixed chart axis bounds — see drawChart() for why these aren't per-selection
  const GLOBAL_MAX_H_KOE = 50;
  const GLOBAL_MAX_B_T = 1.6;

  /* ---------------------------------------------------------------
   * UI catalog — names, labels, and dropdown-filtering rules only.
   * No Br/Hci/temperature-coefficient values live here; those are
   * fetched from the Worker per selection.
   * ------------------------------------------------------------- */

  const NEO_GRADE_ORDER = ['N28','N30','N33','N35','N38','N40','N42','N45','N48','N50','N52'];
  const NEO_FAMILY_ORDER = ['Normal','M','H','SH','UH','EH','AH'];
  const NEO_FAMILY_META = {
    Normal: { label: 'N (80°C)',  minGrade: 35, maxGrade: 52 },
    M:      { label: 'M (100°C)',      minGrade: 35, maxGrade: 52 },
    H:      { label: 'H (120°C)',      minGrade: 35, maxGrade: 50 },
    SH:     { label: 'SH (150°C)',     minGrade: 33, maxGrade: 48 },
    UH:     { label: 'UH (180°C)',     minGrade: 30, maxGrade: 45 },
    EH:     { label: 'EH (200°C)',     minGrade: 30, maxGrade: 40 },
    AH:     { label: 'AH (240°C)',     minGrade: 28, maxGrade: 35 },
  };
  const gradeNum = (g) => parseInt(g.slice(1), 10);

  const ALNICO_PROCESSES = ['Cast', 'Sintered'];
  const ALNICO_GRADES_BY_PROCESS = {
    Cast: ['LN9','LN10','LNG12','LNG13','LNG37','LNG40','LNG44','LNG52','LNG60','LNGT28','LNGT36J','LNGT18','LNGT32','LNGT40','LNGT60','LNGT75'],
    Sintered: ['FLN8','FLNG12','FLNGT14','FLNGT18','FLNG28','FLNG34','FLNGT28','FLNGT31','FLNG33J','FLNGT38','FLNGT42'],
  };
  const ALNICO_DEFAULT_GRADE = { Cast: 'LNG44', Sintered: 'FLNG34' };

  const SMCO_SERIES_ORDER = ['SmCo5', 'Sm2Co17'];
  const SMCO_GRADES_BY_SERIES = {
    SmCo5: ['S16','S18','S20','S22','S24'],
    Sm2Co17: ['S220','S240','S260','S280','S300','S320'],
  };
  const SMCO_DEFAULT_GRADE = { SmCo5: 'S22', Sm2Co17: 'S300' };

  const FERRITE_GRADE_ORDER = ['Y10','Y10T','Y20','Y22H','Y23','Y25','Y25BH','Y26H','Y27H','Y28','Y30','Y30BH','Y32','Y33','Y35','Y35H1','Y35H2','Y35H3','Y35H-4H','Y38B','Y38H','Y40E','Y40B','Y45E','Y45B'];
  const FERRITE_DEFAULT_GRADE = 'Y30BH';

  /* ---------------------------------------------------------------
   * DOM refs + generic 2-slot sub-dropdown row
   * ------------------------------------------------------------- */

  const materialSelect = $('bhc-material');
  const subRow = $('bhc-subRow');
  const sub1Label = $('bhc-sub1Label');
  const sub1Select = $('bhc-sub1');
  const sub2Col = $('bhc-sub2Col');
  const sub2Label = $('bhc-sub2Label');
  const sub2Select = $('bhc-sub2');
  const tempSlider = $('bhc-temp');
  const tempVal = $('bhc-tempVal');

  const opt = (value, label) => { const o = document.createElement('option'); o.value = value; o.textContent = label; return o; };

  materialSelect.appendChild(opt('Neodymium', 'Neodymium'));
  materialSelect.appendChild(opt('AlNiCo', 'AlNiCo'));
  materialSelect.appendChild(opt('SmCo', 'SmCo'));
  materialSelect.appendChild(opt('Ceramic', 'Ceramic / Ferrite'));
  materialSelect.value = 'Neodymium';

  let sweepCache = null; // { sweep: [{T,Br,Hci,Hk}], Tw }
  let fetchController = null;
  let debounceTimer = null;

  /* ---------------------------------------------------------------
   * Per-material sub-dropdown population
   * ------------------------------------------------------------- */

  function populateForMaterial() {
    const mat = materialSelect.value;
    sub1Select.innerHTML = '';
    sub2Select.innerHTML = '';

    if (mat === 'Neodymium') {
      sub1Label.textContent = 'Grade';
      sub2Label.textContent = 'Op Temp Limit';
      sub2Col.style.display = '';
      NEO_GRADE_ORDER.forEach(g => sub1Select.appendChild(opt(g, g)));
      sub1Select.value = 'N52';
      populateNeoFamilies();
    } else if (mat === 'AlNiCo') {
      sub1Label.textContent = 'Process';
      sub2Label.textContent = 'Grade';
      sub2Col.style.display = '';
      ALNICO_PROCESSES.forEach(p => sub1Select.appendChild(opt(p, p)));
      sub1Select.value = 'Cast';
      populateAlnicoGrades();
    } else if (mat === 'SmCo') {
      sub1Label.textContent = 'Series';
      sub2Label.textContent = 'Grade';
      sub2Col.style.display = '';
      SMCO_SERIES_ORDER.forEach(s => sub1Select.appendChild(opt(s, s)));
      sub1Select.value = 'Sm2Co17';
      populateSmcoGrades();
    } else if (mat === 'Ceramic') {
      sub1Label.textContent = 'Grade';
      sub2Col.style.display = 'none';
      FERRITE_GRADE_ORDER.forEach(g => sub1Select.appendChild(opt(g, g)));
      sub1Select.value = FERRITE_DEFAULT_GRADE;
    }
  }

  function populateNeoFamilies() {
    const gNum = gradeNum(sub1Select.value);
    const prev = sub2Select.value;
    sub2Select.innerHTML = '';
    NEO_FAMILY_ORDER.forEach(fam => {
      const f = NEO_FAMILY_META[fam];
      if (gNum >= f.minGrade && gNum <= f.maxGrade) sub2Select.appendChild(opt(fam, f.label));
    });
    const stillValid = Array.from(sub2Select.options).some(o => o.value === prev);
    sub2Select.value = stillValid ? prev : sub2Select.options[0].value;
  }

  function populateAlnicoGrades() {
    const process = sub1Select.value;
    sub2Select.innerHTML = '';
    ALNICO_GRADES_BY_PROCESS[process].forEach(g => sub2Select.appendChild(opt(g, g)));
    sub2Select.value = ALNICO_DEFAULT_GRADE[process];
  }

  function populateSmcoGrades() {
    const series = sub1Select.value;
    sub2Select.innerHTML = '';
    SMCO_GRADES_BY_SERIES[series].forEach(g => sub2Select.appendChild(opt(g, g)));
    sub2Select.value = SMCO_DEFAULT_GRADE[series];
  }

  populateForMaterial();

  /* ---------------------------------------------------------------
   * Event wiring
   * ------------------------------------------------------------- */

  materialSelect.addEventListener('change', () => {
    populateForMaterial();
    scheduleFetch(0);
  });
  sub1Select.addEventListener('change', () => {
    const mat = materialSelect.value;
    if (mat === 'Neodymium') populateNeoFamilies();
    else if (mat === 'AlNiCo') populateAlnicoGrades();
    else if (mat === 'SmCo') populateSmcoGrades();
    scheduleFetch(0);
  });
  sub2Select.addEventListener('change', () => scheduleFetch(0));
  tempSlider.addEventListener('input', () => {
    tempVal.textContent = tempSlider.value + '\u00b0C';
    render();
  });

  function scheduleFetch(delayMs) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchSweep, delayMs === undefined ? 150 : delayMs);
  }

  /* ---------------------------------------------------------------
   * Fetching
   * ------------------------------------------------------------- */

  async function fetchSweep() {
    if (!API_URL) return;
    if (fetchController) fetchController.abort();
    fetchController = new AbortController();

    const mat = materialSelect.value;
    const body = mat === 'Ceramic'
      ? { material: mat, sub1: sub1Select.value, sub2: null }
      : { material: mat, sub1: sub1Select.value, sub2: sub2Select.value };

    root.querySelector('.stat-grid-top').style.opacity = '0.5';
    $('bhc-fetchError').style.display = 'none';

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: fetchController.signal,
      });
      if (!res.ok) throw new Error('bad response');
      sweepCache = await res.json();
      updateTempSliderRange();
      render();
    } catch (e) {
      if (e.name === 'AbortError') return;
      $('bhc-fetchError').style.display = 'block';
    } finally {
      root.querySelector('.stat-grid-top').style.opacity = '1';
    }
  }

  function updateTempSliderRange() {
    if (!sweepCache) return;
    const max = Math.max(200, sweepCache.Tw + 100);
    tempSlider.max = max;
    if (parseFloat(tempSlider.value) > max) tempSlider.value = max;
    tempVal.textContent = tempSlider.value + '\u00b0C';
  }

  function lookupSweepPoint(T) {
    if (!sweepCache || !sweepCache.sweep.length) return null;
    // sweep is on a 5°C grid; snap to nearest available point
    let best = sweepCache.sweep[0], bestDist = Infinity;
    for (const p of sweepCache.sweep) {
      const d = Math.abs(p.T - T);
      if (d < bestDist) { best = p; bestDist = d; }
    }
    return best;
  }

  /* ---------------------------------------------------------------
   * Curve point generation (generic knee-shape math; not proprietary —
   * the Worker already supplied the real Br/Hci/Hk for this selection)
   * ------------------------------------------------------------- */

  function bAt(Hmag, Br, Hci, Hk) {
    if (Hmag <= Hk) return Br;
    if (Hmag >= Hci) return 0;
    const t = (Hmag - Hk) / (Hci - Hk);
    return Br * Math.pow(1 - t, 2.2);
  }

  function buildCurvePoints(Br, Hci, Hk, n) {
    n = n || 80;
    const pts = [];
    for (let i = 0; i <= n; i++) {
      const Hmag = (Hci * i) / n;
      pts.push({ H: -Hmag, B: bAt(Hmag, Br, Hci, Hk) });
    }
    return pts;
  }

  /* ---------------------------------------------------------------
   * Chart + stats rendering
   * ------------------------------------------------------------- */

  function drawChart(point, refPoint20) {
    const svg = $('bhc-chartSvg');
    const { Br, Hci, Hk } = point;
    const pts = buildCurvePoints(Br, Hci, Hk);

    const padL = 60, padR = 20, padT = 20, padB = 40;
    const W = 640, H = 380;
    const plotW = W - padL - padR, plotH = H - padT - padB;

    // Fixed axes across every material/grade/temperature combination, rather
    // than autoscaling per selection — so switching materials shows the real
    // difference in scale (e.g. AlNiCo's tiny Hci vs. Neo AH-grade's huge Hci)
    // instead of each getting its own zoomed-in view. Bounds cover the actual
    // worst case (coldest slider point, -40°C, where Br/Hci peak for most
    // materials) with a small margin.
    const maxH = GLOBAL_MAX_H_KOE;
    const maxB = GLOBAL_MAX_B_T;

    const xOf = (Hval) => padL + (1 - (-Hval) / maxH) * plotW;
    const yOf = (Bval) => padT + plotH - (Bval / maxB) * plotH;

    const path = pts.map((p, i) => (i === 0 ? 'M' : 'L') + xOf(p.H).toFixed(1) + ',' + yOf(p.B).toFixed(1)).join(' ');

    let grid = '';
    const hTicks = 5, bTicks = 4;
    for (let i = 0; i <= hTicks; i++) {
      const hv = -(maxH * i) / hTicks;
      const x = xOf(hv);
      grid += `<line x1="${x}" y1="${padT}" x2="${x}" y2="${padT+plotH}" stroke="#1F2733" stroke-width="1"/>`;
      grid += `<text x="${x}" y="${padT+plotH+18}" text-anchor="middle" font-family="JetBrains Mono" font-size="10" fill="#565F70">${hv.toFixed(0)}</text>`;
    }
    for (let i = 0; i <= bTicks; i++) {
      const bv = (maxB * i) / bTicks;
      const y = yOf(bv);
      grid += `<line x1="${padL}" y1="${y}" x2="${padL+plotW}" y2="${y}" stroke="#1F2733" stroke-width="1"/>`;
      grid += `<text x="${padL-8}" y="${y+4}" text-anchor="end" font-family="JetBrains Mono" font-size="10" fill="#565F70">${bv.toFixed(1)}</text>`;
    }

    let refPath = '';
    if (Math.round(parseFloat(tempSlider.value)) !== 20) {
      const refPts = buildCurvePoints(refPoint20.Br, refPoint20.Hci, refPoint20.Hk);
      const rp = refPts.map((p, i) => (i === 0 ? 'M' : 'L') + xOf(p.H).toFixed(1) + ',' + yOf(p.B).toFixed(1)).join(' ');
      refPath = `<path d="${rp}" fill="none" stroke="var(--text-faint)" stroke-width="1.5" stroke-dasharray="5,4" opacity="0.7"/>`;
    }

    const kneeMarker = `<circle cx="${xOf(-Hk)}" cy="${yOf(bAt(Hk, Br, Hci, Hk))}" r="4" fill="var(--accent)" stroke="#0B0E14" stroke-width="1.5"/>`;

    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.innerHTML = `
      ${grid}
      <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT+plotH}" stroke="#262E3D"/>
      <line x1="${padL}" y1="${padT+plotH}" x2="${padL+plotW}" y2="${padT+plotH}" stroke="#262E3D"/>
      ${refPath}
      <path d="${path}" fill="none" stroke="var(--accent)" stroke-width="2.5"/>
      ${kneeMarker}
      <text x="${padL+plotW/2}" y="${H-4}" text-anchor="middle" font-family="JetBrains Mono" font-size="11" fill="#8B93A1">Applied Field H (kOe)</text>
      <text x="14" y="${padT+plotH/2}" text-anchor="middle" font-family="JetBrains Mono" font-size="11" fill="#8B93A1" transform="rotate(-90 14 ${padT+plotH/2})">Induction B (T)</text>
    `;
  }

  function render() {
    if (!sweepCache) return;
    const T = parseFloat(tempSlider.value);
    const point = lookupSweepPoint(T);
    const refPoint20 = lookupSweepPoint(20);
    if (!point || !refPoint20) return;

    $('bhc-brValue').textContent = point.Br.toFixed(3) + '\u2009T  (' + (point.Br * 10).toFixed(2) + '\u2009kG)';
    $('bhc-hciValue').textContent = point.Hci.toFixed(2) + '\u2009kOe  (' + (point.Hci * KOE_TO_KAM).toFixed(0) + '\u2009kA/m)';

    const warn = $('bhc-tempWarning');
    if (T > sweepCache.Tw) {
      warn.style.display = 'block';
      warn.textContent = '\u26a0 Above this selection\u2019s typical rated working temperature (' + sweepCache.Tw + '\u00b0C) \u2014 the knee shown here is a simplified illustration of demagnetization risk, not a precise prediction.';
    } else {
      warn.style.display = 'none';
    }

    drawChart(point, refPoint20);
  }

  fetchSweep();
})();
